import { useEffect, useState } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import { useAuth } from "context/authContext";
import AuthService from "../../services/AuthService";
import AccessCodeService from "services/AccessCodeService";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { responsiveFontSizes } from "@mui/material";
import { substr } from "stylis";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const { logout } = useAuth();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const collapseName = location.pathname.replace("/", "");
  const currentUser = AuthService.getCurrentUser();
  
  let isAdmin = false;
  let unlogged = true;

  if (currentUser) {
    isAdmin = currentUser.Role.Description.toLowerCase() === 'admin'
    unlogged = currentUser.Role.Description.toLowerCase() === 'unloggeduser'
  } 

  const keysMenu = [
    'usuarios',
    'Usuarios',
    'Exportar',
    'exportar',
    'administrador',
    'Administrador'
  ];

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key.toLowerCase() === collapseName.toLowerCase()} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    const menuKey = key.toLowerCase();
    if (unlogged) {
      return
    }
    else if (!isAdmin && keysMenu.includes(menuKey)) {
      return
    }
    else if (isAdmin && ['pedidos', 'ofertas'].includes(menuKey)){
      return
    }

    return returnValue;
  });

  const getUserName = () => {
    if (currentUser?.Name) {
      return currentUser.Name;
    }

    const guestDisplayName = AccessCodeService.getGuestDisplayName();
    if (guestDisplayName) {
      return guestDisplayName;
    }

    if (unlogged) {
      return "Acesso por codigo";
    }

    return "";
  };

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={2} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>

      <MDBox p={2} mt="auto">
        <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor} 
          sx={{
            textAlign: "center",
            wordBreak: "break-word", // força quebra quando passar
            whiteSpace: "normal",
            mb: 2,
            fontSize: "0.7rem",
          }}>
            {getUserName()}
        </MDTypography>
        
        {unlogged 
          ? 
            <div style={{display: 'flex', flexDirection: 'column', gap: 5}}>
              <MDButton
                component="a"
                rel="noreferrer"
                variant="gradient"
                color={sidenavColor}
                onClick={() => logout()}
                fullWidth
              >
                sair
              </MDButton>

              <MDButton
                component="a"
                rel="noreferrer"
                variant="gradient"
                color={sidenavColor}
                onClick={() => navigate('/login')}
                fullWidth
              >
                entrar
              </MDButton>
              
              <MDButton
                component="a"
                rel="noreferrer"
                variant="gradient"
                color={sidenavColor}
                onClick={() => navigate('/sign-up')}
                fullWidth
              >
                cadastrar-se
              </MDButton>
            </div>
          :
            <MDButton
              component="a"
              rel="noreferrer"
              variant="gradient"
              color={sidenavColor}
              onClick={() => logout()}
              fullWidth
            >
              sair
            </MDButton>
        }
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
